import { useEffect, useState } from "react";
import { Link, NavLink, useBasePath } from "../lib/router";
import { useNavigate as useRouterNavigate, useLocation } from "react-router-dom";
import { withLang } from "../lib/router";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";
import { LogoFull } from "./Logo";
import { EASE } from "./motion";

const DARK_HERO_PATHS = ["/", "/fuer-studios"];

export const Navbar = () => {
  const { lang, setLang, t } = useLanguage();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const pathname = useBasePath();
  const routerNavigate = useRouterNavigate();
  const { search } = useLocation();
  const switchLang = (code) => {
    setLang(code);
    routerNavigate(withLang(pathname, code) + search, { replace: true });
  };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 48);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  const darkHero = DARK_HERO_PATHS.includes(pathname);
  const light = open || (darkHero && !scrolled);
  const tone = light ? "text-[#FAF8F5]" : "text-[#1C1C1A]";

  const links = [
    { to: "/", label: t.nav.home, testid: "nav-home" },
    { to: "/reformer", label: t.nav.reformer, testid: "nav-reformer" },
    { to: "/equipment", label: t.nav.equipment, testid: "nav-equipment" },
    { to: "/fuer-studios", label: t.nav.studios, testid: "nav-studios" },
    { to: "/ueber-uns", label: t.nav.about, testid: "nav-about" },
    { to: "/kontakt", label: t.nav.contact, testid: "nav-contact" },
  ];

  const renderLangToggle = (cls, prefix = "nav-lang") => (
    <div className={`flex items-center gap-2 text-[11px] font-mono tracking-[0.2em] ${cls}`}>
      {["de", "en", "fr", "es"].map((code, i) => (
        <span key={code} className="flex items-center gap-2">
          {i > 0 && <span className="opacity-30">/</span>}
          <button
            data-testid={`${prefix}-${code}`}
            onClick={() => switchLang(code)}
            className={`transition-opacity duration-300 ${lang === code ? "opacity-100" : "opacity-40 hover:opacity-90"}`}
          >
            {code.toUpperCase()}
          </button>
        </span>
      ))}
    </div>
  );

  return (
    <>
      <header
        data-testid="navbar"
        className={`fixed inset-x-0 top-0 z-50 transition-colors duration-500 ${
          scrolled && !open
            ? "bg-[#FAF8F5]/85 backdrop-blur-xl border-b border-[#1C1C1A]/10"
            : "bg-transparent"
        }`}
      >
        <nav className="flex h-20 items-center justify-between px-6 md:px-12 lg:px-20">
          <Link to="/" data-testid="nav-logo" className={`flex items-center gap-3 ${tone} transition-colors duration-500`}>
            <LogoFull className="h-9 w-auto md:h-14" variant={light ? "light" : "dark"} />
          </Link>

          <div className="hidden items-center gap-9 lg:flex">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                data-testid={l.testid}
                className={({ isActive }) =>
                  `relative text-[11px] uppercase tracking-[0.22em] font-medium transition-opacity duration-300 ${tone} ${
                    isActive ? "opacity-100" : "opacity-55 hover:opacity-100"
                  }`
                }
              >
                {({ isActive }) => (
                  <span className="flex flex-col items-center gap-1.5">
                    {l.label}
                    <span
                      className={`h-1 w-1 rounded-full bg-[#C5A059] transition-opacity duration-300 ${
                        isActive ? "opacity-100" : "opacity-0"
                      }`}
                    />
                  </span>
                )}
              </NavLink>
            ))}
          </div>

          <div className="flex items-center gap-5 md:gap-7">
            {renderLangToggle(`hidden md:flex ${tone}`)}
            <Link
              to="/anfrage"
              data-testid="nav-quote-button"
              className={`hidden md:inline-flex items-center border px-5 py-2.5 text-[10px] uppercase tracking-[0.25em] font-medium transition-colors duration-500 ${
                light
                  ? "border-[#FAF8F5]/50 text-[#FAF8F5] hover:bg-[#FAF8F5] hover:text-[#1C1C1A]"
                  : "border-[#1C1C1A]/30 text-[#1C1C1A] hover:bg-[#1C1C1A] hover:text-[#FAF8F5]"
              }`}
            >
              {t.nav.cta}
            </Link>
            <button
              data-testid="nav-menu-toggle"
              onClick={() => setOpen(!open)}
              aria-label={open ? t.nav.close : t.nav.menu}
              className={`lg:hidden ${tone} transition-colors duration-500`}
            >
              {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </nav>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            className="fixed inset-0 z-40 flex flex-col bg-[#1C1C1A] text-[#FAF8F5]"
          >
            <div className="flex flex-1 flex-col justify-center gap-1 px-8">
              {links.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, y: 26 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 + i * 0.07, duration: 0.6, ease: EASE }}
                >
                  <NavLink
                    to={l.to}
                    end={l.to === "/"}
                    data-testid={`mobile-${l.testid}`}
                    className="block py-2.5 font-serif text-4xl font-light tracking-tight"
                  >
                    {l.label}
                  </NavLink>
                </motion.div>
              ))}
            </div>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.55, duration: 0.6 }}
              className="flex flex-wrap items-center justify-between gap-6 px-8 pb-12"
            >
              {renderLangToggle("text-[#FAF8F5]", "mobile-lang")}
              <Link
                to="/anfrage"
                data-testid="mobile-nav-quote-button"
                className="inline-flex items-center border border-[#FAF8F5]/40 px-6 py-3 text-[11px] uppercase tracking-[0.25em] font-medium"
              >
                {t.nav.cta}
              </Link>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
